import React, { useState, useEffect, useMemo } from 'react';
import { LucideIcon } from '../UI/LucideIcon';

// ── Avisos por e-mail de arquivo novo nas emissões FAVORITAS. Padrão
// (desde 05/10/2026): favoritar já liga os 4 tipos — a pessoa desmarca o que
// não quer em Meu perfil › Avisos por e-mail (BalaoFavorito lembra disso
// depois de cada estrela).
// AvisoNovidadeNotificacoes apresenta a novidade no acesso, por tempo
// limitado.

// Chaves do backend (sem acento) → rótulo. Mesma lista da matriz do Perfil.
export const TIPOS_AVISO = [
  { key: 'Documentos',  label: 'Documentos' },
  { key: 'Relatorios',  label: 'Relatórios' },
  { key: 'Comunicados', label: 'Comunicados' },
  { key: 'Assembleia',  label: 'Assembleia' },
];

// Remetente dos avisos — a pessoa precisa marcá-lo como confiável, senão o
// e-mail tende a cair no spam/lixo eletrônico.
export const REMETENTE_AVISOS = 'DoNotReply@leveragesec.com.br';

// ── RemetenteConfiavel — caixa "os avisos chegam de X, marque como
// confiável", com botão de copiar o endereço (usado no Perfil).
export function RemetenteConfiavel() {
  const [copiado, setCopiado] = useState(false);
  useEffect(() => {
    if (!copiado) return undefined;
    const t = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(t);
  }, [copiado]);
  function copiar() {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(REMETENTE_AVISOS).then(() => setCopiado(true)).catch(() => {});
  }
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(255,189,46,.12)', border: '1px solid rgba(255,189,46,.45)', borderRadius: 12, padding: '12px 14px', fontSize: 12.5, color: 'var(--gray-700)', lineHeight: 1.55 }}>
      <LucideIcon name="mail" size={16} color="#a87600" style={{ flexShrink: 0, marginTop: 2 }}/>
      <div style={{ minWidth: 0 }}>
        <div>
          Os avisos são enviados por{' '}
          <b style={{ color: 'var(--brand-indigo)', wordBreak: 'break-all' }}>{REMETENTE_AVISOS}</b>
          {navigator.clipboard && (
            <button type="button" className="lev-link-btn" onClick={copiar} style={{ display: 'inline-flex', fontSize: 12, marginLeft: 6, verticalAlign: 'middle' }}>
              <LucideIcon name={copiado ? 'check' : 'copy'} size={12}/> {copiado ? 'Copiado' : 'Copiar'}
            </button>
          )}
        </div>
        <div style={{ marginTop: 4 }}>
          Para os e-mails não irem para o spam ou lixo eletrônico, adicione esse endereço aos seus contatos ou à lista de remetentes confiáveis do seu e-mail — e, se algum aviso cair no spam, marque como "Não é spam". Se sua empresa filtra e-mails externos, peça à equipe de TI para liberar esse remetente.
        </div>
      </div>
    </div>
  );
}

// ── Balão pós-estrela — em vez de perguntar a cada estrela (ruim pra quem
// marca várias seguidas), mostra um balão no canto inferior lembrando que o
// aviso por e-mail foi ligado e se ajusta no perfil. Fica BALAO_MS e fecha sozinho (barra
// de contagem regressiva) ou no X. Vive no Shell (App.jsx), então não some
// ao trocar de página; o prazo fica no módulo, então sobrevive até a um
// remount. Nova estrela com o balão aberto reinicia a contagem.
export const BALAO_MS = 25000;
const EVENTO_BALAO = 'lev:balao-favorito';
let balaoFimEm = 0; // Date.now() em que o balão fecha (0 = fechado)

export function mostrarBalaoFavorito() {
  balaoFimEm = Date.now() + BALAO_MS;
  window.dispatchEvent(new Event(EVENTO_BALAO));
}

export function BalaoFavorito({ onIrParaPerfil }) {
  const [fimEm, setFimEm] = useState(balaoFimEm);
  useEffect(() => {
    const abrir = () => setFimEm(balaoFimEm);
    window.addEventListener(EVENTO_BALAO, abrir);
    return () => window.removeEventListener(EVENTO_BALAO, abrir);
  }, []);
  const fechar = () => { balaoFimEm = 0; setFimEm(0); };
  return (
    <CartaoBalao fimEm={fimEm} duracaoMs={BALAO_MS} onFechar={fechar} titulo="Avisos por e-mail ligados">
      Ao favoritar, você passa a receber um e-mail sempre que um arquivo novo for disponibilizado no site para essa emissão (Documentos, Relatórios, Comunicados e Assembleia). Para escolher o que não quer receber, vá em <b>Meu perfil › Avisos por e-mail</b> e desmarque.
      <div>
        <button type="button" className="lev-link-btn" style={{ marginTop: 8, fontSize: 12.5 }} onClick={() => { fechar(); onIrParaPerfil(); }}>
          Ir para o perfil <LucideIcon name="arrow-right" size={13}/>
        </button>
      </div>
    </CartaoBalao>
  );
}

// ── Balão de aviso genérico — mesmo cartão do pós-estrela, com título/texto
// livres (ex.: lembrete depois de baixar uma planilha, erro de download).
// Mesmo esquema do BalaoFavorito: estado no módulo + evento, renderizado 1x
// no Shell (App.jsx). Novo aviso com um aberto substitui e reinicia a contagem.
export const BALAO_AVISO_MS = 15000;
const EVENTO_BALAO_AVISO = 'lev:balao-aviso';
let balaoAviso = null; // { titulo, texto, icone, erro, duracaoMs, fimEm }

export function mostrarBalaoAviso({ titulo, texto, icone = 'info', erro = false, duracaoMs = BALAO_AVISO_MS }) {
  balaoAviso = { titulo, texto, icone: erro ? 'alert-triangle' : icone, erro, duracaoMs, fimEm: Date.now() + duracaoMs };
  window.dispatchEvent(new Event(EVENTO_BALAO_AVISO));
}

export function BalaoAviso() {
  const [aviso, setAviso] = useState(balaoAviso);
  useEffect(() => {
    const abrir = () => setAviso(balaoAviso);
    window.addEventListener(EVENTO_BALAO_AVISO, abrir);
    return () => window.removeEventListener(EVENTO_BALAO_AVISO, abrir);
  }, []);
  if (!aviso) return null;
  const fechar = () => { balaoAviso = null; setAviso(null); };
  return (
    <CartaoBalao fimEm={aviso.fimEm} duracaoMs={aviso.duracaoMs} onFechar={fechar}
      titulo={aviso.titulo} icone={aviso.icone} erro={aviso.erro}>
      {aviso.texto}
    </CartaoBalao>
  );
}

// Casca visual + contagem regressiva compartilhada pelos balões. Fecha
// sozinho em `fimEm` (chama onFechar) ou no X.
function CartaoBalao({ fimEm, duracaoMs, onFechar, titulo, icone = 'bell', erro = false, children }) {
  // Calculado 1x por contagem — re-render do Shell (troca de página) não
  // pode mexer na duração da barra no meio da animação.
  const restante = useMemo(() => fimEm - Date.now(), [fimEm]);
  useEffect(() => {
    if (restante <= 0) return undefined;
    const t = setTimeout(onFechar, restante);
    return () => clearTimeout(t);
  }, [restante]); // eslint-disable-line react-hooks/exhaustive-deps

  if (restante <= 0) return null;
  const cor = erro ? '#d63333' : 'var(--brand-indigo)';
  return (
    <div className="lev-balao" role={erro ? 'alert' : 'status'} aria-live={erro ? 'assertive' : 'polite'}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 14px 14px 16px' }}>
        <span style={{ width: 34, height: 34, borderRadius: 10, background: erro ? 'rgba(214,51,51,.10)' : 'rgba(52,62,131,.10)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LucideIcon name={icone} size={17} color={cor}/>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: cor, fontFamily: 'var(--font-display)' }}>{titulo}</div>
          <div style={{ fontSize: 12.5, color: 'var(--gray-600)', lineHeight: 1.5, marginTop: 4 }}>
            {children}
          </div>
        </div>
        <button type="button" onClick={onFechar} aria-label="Fechar"
          style={{ border: 'none', background: 'none', padding: 2, cursor: 'pointer', color: 'var(--gray-400)', lineHeight: 0, flexShrink: 0 }}>
          <LucideIcon name="x" size={16}/>
        </button>
      </div>
      {/* key = fimEm: reinicia a barra quando a contagem reinicia. Duração =
          o que falta (remount no meio da contagem continua de onde parou). */}
      <div className="lev-balao-barra" key={fimEm} style={{ animationDuration: `${restante}ms`, width: `${(restante / duracaoMs) * 100}%` }}/>
    </div>
  );
}

// ── AvisoNovidadeNotificacoes — apresenta a novidade (aviso por e-mail de
// arquivo novo nas favoritas, que nunca existiu em produção) e manda a
// pessoa ajustar no perfil — favorita já vem com tudo ligado. Aparece até a
// pessoa dispensar (guardado por e-mail no navegador) e só até
// AVISO_NOVIDADE_ATE — depois some sozinho para todo mundo.
// AJUSTAR: 1 mês a partir do dia da publicação.
export const AVISO_NOVIDADE_ATE = '2026-11-02';
const CHAVE_AVISO = 'lev_aviso_notificacoes_v1';

function lerDispensados() {
  try { return JSON.parse(localStorage.getItem(CHAVE_AVISO) || '[]'); } catch (e) { return []; }
}

export function AvisoNovidadeNotificacoes({ email, onIrParaPerfil }) {
  const ativo = useMemo(() => new Date() < new Date(`${AVISO_NOVIDADE_ATE}T23:59:59`), []);
  const chave = (email || '').toLowerCase();
  const [aberto, setAberto] = useState(() => ativo && !!chave && !lerDispensados().includes(chave));

  function dispensar() {
    try {
      const lista = lerDispensados();
      if (!lista.includes(chave)) localStorage.setItem(CHAVE_AVISO, JSON.stringify([...lista, chave]));
    } catch (e) { /* sem storage — volta a aparecer no próximo acesso */ }
    setAberto(false);
  }

  useEffect(() => {
    if (!aberto) return undefined;
    const onKey = e => { if (e.key === 'Escape') dispensar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!aberto) return null;
  return (
    <div className="lev-overlay" onClick={dispensar}>
      <div className="lev-modal" role="dialog" aria-modal="true" aria-labelledby="aviso-novidade-titulo"
        style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="lev-modal-bar">
          <button className="lev-modal-close" onClick={dispensar} aria-label="Fechar"><LucideIcon name="x" size={18}/></button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(158,54,239,.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <LucideIcon name="bell" size={19} color="var(--brand-purple)"/>
          </span>
          <div id="aviso-novidade-titulo" style={{ fontSize: 17, fontWeight: 700, color: 'var(--brand-indigo)', fontFamily: 'var(--font-display)' }}>Novidade: avisos por e-mail</div>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--gray-600)', lineHeight: 1.55, margin: '0 0 10px' }}>
          Agora você pode receber um e-mail sempre que um novo documento for publicado nas suas emissões favoritas (as marcadas com a estrela).
        </p>
        <p style={{ fontSize: 13.5, color: 'var(--gray-600)', lineHeight: 1.55, margin: 0 }}>
          Ao favoritar, você recebe todos os avisos daquela emissão. Se não quiser receber algum tipo, acesse <b>Meu perfil › Avisos por e-mail</b> e desmarque.
        </p>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button type="button" className="lev-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={dispensar}>Agora não</button>
          <button type="button" className="lev-btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { dispensar(); onIrParaPerfil(); }}>
            <LucideIcon name="user" size={15} color="#fff"/> Ir para o perfil
          </button>
        </div>
      </div>
    </div>
  );
}
